import { useEffect, useState } from 'react';
import {
  ArrowDownToLine,
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronRight,
  CircleGauge,
  ClipboardCheck,
  Copy,
  Cpu,
  Download,
  ExternalLink,
  FileCheck2,
  Gamepad2,
  Gauge,
  Globe2,
  HardDrive,
  LockKeyhole,
  Menu,
  MonitorCheck,
  Power,
  Radar,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  X,
  Zap
} from 'lucide-react';
import ProductPreview from './components/ProductPreview';
import {
  CleanerVisual,
  DetectionVisual,
  FpsVisual,
  LanguageVisual,
  RestoreVisual,
  SafetyVisual
} from './components/FeatureVisuals';

const downloadPath = '/downloads/WFast-Setup-1.0.0.exe';
const sha256 = '2643D9E2665DD49C70E73F04EF70BCCEC5F7B1CE51F9C9D86D256616F523898F';

const navigation = [
  ['Özellikler', '#ozellikler'],
  ['Oyun Modu', '#oyun-modu'],
  ['Güvenlik', '#guvenlik'],
  ['Tüm Özellikler', '#tum-ozellikler'],
  ['SSS', '#sss']
];

const focusSteps = [
  { icon: Radar, number: '01', title: 'Oyunu algılar', text: 'Launcher yerine asıl oyun sürecini otomatik bulur.' },
  { icon: Power, number: '02', title: 'Sistemi hazırlar', text: 'Güç planı ve işlem öncelikleri güvenle düzenlenir.' },
  { icon: Zap, number: '03', title: 'Kaynağı odaklar', text: 'High QoS ve seçici arka plan yönetimi devreye girer.' },
  { icon: CircleGauge, number: '04', title: 'FPS’yi ölçer', text: 'Sağ üst katman gerçek kare hızını canlı gösterir.' },
  { icon: RefreshCw, number: '05', title: 'Her şeyi geri alır', text: 'Oyun kapandığında önceki sistem durumu geri yüklenir.' }
];

const capabilityGroups = [
  {
    id: 'system',
    icon: CircleGauge,
    number: '01',
    short: 'Sistem Denetimi',
    title: 'Bilgisayarınızın durumunu tek panelden görün.',
    description: 'WFast yalnızca işlem uygulamaz; önce sistemi ölçer, anlaşılır grafiklere dönüştürür ve karar vermeniz için gerçek veriyi gösterir.',
    features: [
      ['Canlı CPU ölçümü', 'Toplam işlemci kullanımı düzenli aralıklarla yenilenir.'],
      ['Bellek görünümü', 'Kullanılan, boş ve toplam RAM ile kullanım yüzdesi gösterilir.'],
      ['Disk denetimi', 'Sabit sürücülerin toplam, boş ve kullanılan alanı ayrı ayrı izlenir.'],
      ['60 saniyelik grafik', 'Kaynak kullanımındaki değişimler anlık geçmiş grafiğine işlenir.'],
      ['Sistem sağlık puanı', 'CPU, bellek ve disk baskısı tek bir okunabilir skorda özetlenir.'],
      ['En ağır 18 işlem', 'Bellek, CPU süresi ve işlem önceliğiyle birlikte listelenir.'],
      ['Donanım ayrıntıları', 'CPU, mantıksal çekirdek, GPU, sürücü ve fiziksel bellek bilgileri sunulur.'],
      ['Windows ayrıntıları', 'Sürüm, build, son açılış, çalışma süresi ve yönetici yetkisi denetlenir.']
    ],
    metrics: [['CPU · RAM · Disk', 'Canlı kaynaklar'], ['18', 'İşlem görünümü'], ['60 sn', 'Grafik geçmişi']]
  },
  {
    id: 'cleaner',
    icon: Sparkles,
    number: '02',
    short: 'Akıllı Temizlik',
    title: 'Sekiz kontrollü kategoride güvenli alan açın.',
    description: 'Her kategori ayrı seçilir, önce taranır ve sonuçları gösterilir. WFast neyin silineceğini kullanıcıdan gizlemez.',
    features: [
      ['Kullanıcı geçici dosyaları', 'Uygulamaların kullanıcı hesabında bıraktığı geçici içerikler.'],
      ['Windows geçici dosyaları', 'Windows tarafından oluşturulan ve artık kullanılmayan geçici veriler.'],
      ['Çökme raporları', 'CrashDump ve bekleyen Windows hata raporu klasörleri.'],
      ['Tarayıcı önbellekleri', 'Chrome, Edge, Brave ve Firefox önbellekleri; parola ve oturumlar korunur.'],
      ['Grafik önbellekleri', 'DirectX, NVIDIA DX ve GL gölgelendirici önbellekleri.'],
      ['Küçük resim önbelleği', 'Dosya Gezgini’nin gerektiğinde yeniden oluşturabildiği önizleme verileri.'],
      ['Teslim İyileştirme', 'Windows Update dağıtımı için tutulan indirilebilir önbellek.'],
      ['Geri Dönüşüm Kutusu', 'Tüm sürücüler için ayrı uyarı ve açık kullanıcı seçimiyle temizlenir.']
    ],
    metrics: [['8', 'Temizlik kategorisi'], ['Önce tara', 'Dosya ve boyut önizleme'], ['Canlı', 'İşlem ilerlemesi']]
  },
  {
    id: 'detection',
    icon: Radar,
    number: '03',
    short: 'Oyun Algılama',
    title: 'EXE seçmeden gerçek oyun sürecini bulun.',
    description: 'WFast yeni açılan süreçleri, oyun yollarını, kaynak kullanımını ve süreç adını birlikte değerlendirerek launcher yerine asıl oyuna odaklanır.',
    features: [
      ['Otomatik süreç izleme', 'Oyun algılama açıkken yeni başlayan uygulamalar arka planda takip edilir.'],
      ['Platform farkındalığı', 'Steam, Riot, Epic, Xbox, EA, Ubisoft, Battle.net ve GOG yolları tanınır.'],
      ['Valorant ve PUBG ayrımı', 'Launcher sonrasında başlayan gerçek oyun süreçleri özel olarak ayırt edilir.'],
      ['Launcher filtresi', 'Başlatıcı, güncelleyici, web helper ve patcher süreçleri elenir.'],
      ['Anti-cheat koruması', 'Anti-cheat servisleri oyun sanılmaz ve kaynak odağında korunur.'],
      ['Özel oyun desteği', 'Sabit liste dışındaki oyunlar yol, süreç adı ve kaynak davranışıyla puanlanır.'],
      ['Yanlış algılama koruması', 'Windows, üretkenlik ve ağır profesyonel uygulamalar oyun adayından çıkarılır.'],
      ['Kesintisiz bekleme', 'Oyun bittiğinde sistem geri yüklenir ve WFast sıradaki oyunu beklemeye devam eder.']
    ],
    metrics: [['8', 'Tanınan platform'], ['EXE yok', 'Tam otomatik seçim'], ['Akıllı filtre', 'Launcher ve anti-cheat']]
  },
  {
    id: 'focus',
    icon: Gamepad2,
    number: '04',
    short: 'Game Focus',
    title: 'Windows kaynaklarını geçici olarak oyuna odaklayın.',
    description: 'Tüm değişiklikler oyun oturumuna bağlıdır. WFast performans adımlarını uygular, önceki değerleri kaydeder ve oturum sonunda geri getirir.',
    features: [
      ['Yüksek performans güç planı', 'Oyun boyunca işlemci güç tasarrufu sınırlamaları azaltılır.'],
      ['Oyun işlem önceliği', 'Oyun High veya güvenli durumda Above Normal önceliğe taşınır.'],
      ['Windows High QoS', 'Oyun sürecinin güç kısıtlaması geçici olarak performans sınıfına alınır.'],
      ['Otomatik kaynak odağı', 'Tarayıcı, Discord ve Spotify gibi güvenli ağır arka plan uygulamaları seçici yönetilir.'],
      ['Manuel arka plan seçimi', 'Kullanıcının seçtiği işlemler oyun boyunca düşük önceliğe alınabilir.'],
      ['Uygulamaları kapatmaz', 'Arka plan programları sonlandırılmaz; yalnızca geçici öncelik düzenlenir.'],
      ['Kritik süreç koruması', 'Defender, ses, giriş, Windows ve anti-cheat süreçlerine dokunulmaz.'],
      ['Tam geri yükleme', 'Güç planı, QoS ve tüm işlem öncelikleri oyun bitince veya güvenli çıkışta geri alınır.']
    ],
    metrics: [['5 katman', 'Geçici oyun odağı'], ['0', 'Kapatılan uygulama'], ['Otomatik', 'Oturum geri yükleme']]
  },
  {
    id: 'fps',
    icon: Gauge,
    number: '05',
    short: 'FPS Katmanı',
    title: 'Gerçek FPS ve kare süresini oyun içinde izleyin.',
    description: 'WFast oyunun içine kod eklemek yerine Windows kare sunum verilerini dışarıdan ölçer ve sonucu hafif, şeffaf bir katmanda gösterir.',
    features: [
      ['Gerçek zamanlı FPS', 'Kare sunum örneklerinden güncel FPS değeri hesaplanır.'],
      ['Kare süresi', 'Her karenin milisaniye karşılığı FPS ile birlikte gösterilir.'],
      ['Intel PresentMon', 'Windows ETW verileri resmi PresentMon ölçüm motoruyla okunur.'],
      ['DLL enjeksiyonu yok', 'Oyun sürecine kod enjekte edilmez ve oyun dosyaları değiştirilmez.'],
      ['Sağ üst otomatik konum', 'Katman gerçek oyun penceresinin sağ üst köşesini takip eder.'],
      ['Tıklama geçiren tasarım', 'Sayaç fare ve klavye girişlerini engellemez, odağı oyundan almaz.'],
      ['Odak farkındalığı', 'Alt + Tab yapıldığında katman gizlenir, oyuna dönünce yeniden gösterilir.'],
      ['Ekran uyumluluğu', 'Çoklu monitör, DPI ölçeği ve değişen oyun penceresi konumu izlenir.']
    ],
    metrics: [['FPS + ms', 'İki canlı ölçüm'], ['ETW', 'Harici veri okuma'], ['0', 'Oyun dosyası değişikliği']]
  },
  {
    id: 'tray',
    icon: MonitorCheck,
    number: '06',
    short: 'Tepsi ve Kullanım',
    title: 'WFast siz kapatana kadar arka planda hazır kalsın.',
    description: 'Pencereyi kapatmak oyun takibini kesmez. Sistem tepsisi, günlük kullanımı hızlandıran gerçek bir kontrol merkezine dönüşür.',
    features: [
      ['X ile tepsiye küçülme', 'Pencere kapanır ancak oyun algılama ve servisler arka planda çalışır.'],
      ['Tek tıkla geri açma', 'Sağ alttaki WFast simgesine tıklayarak pencere yeniden gösterilir.'],
      ['Dört hızlı kısayol', 'Genel Bakış, Temizlik, Oyun Modu ve İstatistikler doğrudan açılır.'],
      ['Canlı oyun durumu', 'Bekleme, kapalı veya optimize edilen oyunun adı menüde gösterilir.'],
      ['Algılama anahtarı', 'Otomatik oyun algılama tepsi menüsünden açılıp kapatılabilir.'],
      ['Güvenli tam çıkış', 'Tamamen kapat komutu önce aktif performans değişikliklerini geri alır.'],
      ['Tek uygulama örneği', 'WFast yeniden çalıştırılırsa ikinci kopya yerine mevcut pencere açılır.'],
      ['20 dilde yerel menü', 'Tepsi komutları programda seçilen arayüz diliyle birlikte değişir.']
    ],
    metrics: [['4', 'Hızlı sayfa kısayolu'], ['20', 'Tepsi menüsü dili'], ['Arka plan', 'Kesintisiz oyun takibi']]
  },
  {
    id: 'security',
    icon: ShieldCheck,
    number: '07',
    short: 'Güvenlik Sınırları',
    title: 'Ne yaptığını ve neye dokunmadığını bilin.',
    description: 'Temizlikten oyun moduna kadar her modül açık güvenlik sınırlarıyla çalışır. Kalıcı ve kontrolsüz sistem değişiklikleri WFast’ın yaklaşımı değildir.',
    features: [
      ['Kişisel dosyalar kapsam dışı', 'Belgeler, fotoğraflar, oyun kayıtları ve kullanıcı içerikleri hedeflenmez.'],
      ['Tarayıcı verileri korunur', 'Parolalar, çerezler, geçmiş ve açık oturumlar temizleme kapsamına alınmaz.'],
      ['Sembolik bağlantılar izlenmez', 'Tarama tanımlı klasörlerin dışına bağlantılar üzerinden çıkmaz.'],
      ['Kilitli dosyalar atlanır', 'Kullanımda olan veya yetkisiz dosyalar zorlanmaz; hata sayısında raporlanır.'],
      ['Kalıcı tweak yok', 'Kayıt defterine kalıcı FPS ayarları veya geri dönüşsüz servis değişiklikleri uygulanmaz.'],
      ['Windows koruması korunur', 'Defender, Update, ses, giriş ve kritik sistem süreçleri durdurulmaz.'],
      ['İzolasyonlu arayüz', 'Renderer sandbox, context isolation, izin reddi ve dış gezinme engeliyle çalışır.'],
      ['Kullanıcı onayı', 'Riskli kategoriler işaretlenir; temizlik önizleme ve isteğe bağlı onayla başlar.']
    ],
    metrics: [['Yerel', 'Veri işleme'], ['Geri alınır', 'Performans adımları'], ['Açık sınırlar', 'Korunan içerikler']]
  }
];

const faqs = [
  {
    question: 'X düğmesine bastığımda oyun modu durur mu?',
    answer: 'Hayır. X düğmesi WFast’i sağ alttaki sistem tepsisine küçültür; otomatik oyun algılama arka planda çalışmaya devam eder. Tepsi simgesine sağ tıklayarak hızlı sayfalara ulaşabilir, oyun algılamayı yönetebilir veya “WFast’i tamamen kapat” seçeneğiyle uygulamadan güvenle çıkabilirsiniz.'
  },
  {
    question: 'Oyunu veya EXE dosyasını elle seçmem gerekir mi?',
    answer: 'Hayır. WFast çalışan süreçleri ve bilinen oyun platformlarını izler; launcher, anti-cheat ve asıl oyun sürecini birbirinden ayırarak oyunu otomatik algılar. Valorant ve PUBG dahil olmak üzere yalnızca sabit bir oyun listesine bağlı değildir.'
  },
  {
    question: 'WFast FPS artışını garanti eder mi?',
    answer: 'Hayır. Donanım, oyun motoru, grafik ayarları ve darboğazlar her bilgisayarda farklıdır. WFast gereksiz kaynak baskısını azaltır, tutarlı bir oyun odağı kurar ve sonucu canlı verilerle izlemenizi sağlar; gerçek kazanım sisteme göre değişir.'
  },
  {
    question: 'FPS sayacı oyuna kod enjekte eder mi?',
    answer: 'Hayır. FPS katmanı, Intel PresentMon ile kare sunum verilerini dışarıdan okur. Oyun dosyalarını değiştirmez, DLL enjekte etmez ve tıklamaları engellemeyen şeffaf bir pencere olarak çalışır.'
  },
  {
    question: 'Temizlik kişisel dosyalarımı silebilir mi?',
    answer: 'WFast yalnızca tanımlı ve güvenli geçici veri kategorilerini tarar. Belgeler, fotoğraflar, oyun kayıtları ve kişisel klasörler kapsam dışıdır. Temizlik başlamadan önce bulunan dosya sayısı ve boyut gösterilir.'
  },
  {
    question: 'Oyun modu kapandığında ayarlar ne olur?',
    answer: 'Uygulanan güç planı ve işlem öncelikleri kaydedilir. Oyun kapandığında veya oyun modu durdurulduğunda önceki değerler otomatik olarak geri yüklenir.'
  },
  {
    question: 'Hangi Windows sürümleri destekleniyor?',
    answer: 'WFast, 64-bit Windows 10 ve Windows 11 için hazırlanmıştır. Oyun modu gibi sistem düzeyi özellikler yönetici izni ister.'
  }
];

function Logo({ compact = false }) {
  return (
    <a className={`brand${compact ? ' brand-compact' : ''}`} href="#top" aria-label="WFast ana sayfa">
      <span className="brand-mark"><Zap size={19} strokeWidth={2.6} /></span>
      <span className="brand-word">WFast</span>
    </a>
  );
}

function SectionHeading({ eyebrow, title, text, align = 'left' }) {
  return (
    <div className={`section-heading section-heading-${align} reveal`}>
      <span className="eyebrow"><i />{eyebrow}</span>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}

function DownloadButton({ className = '', label = 'WFast’i indir', light = false }) {
  return (
    <a className={`button button-primary${light ? ' button-light' : ''} ${className}`} href={downloadPath} download>
      <ArrowDownToLine size={18} />
      <span>{label}</span>
      <small>v1.0.0</small>
    </a>
  );
}

function CapabilityCenter({ activeId, onSelect }) {
  const active = capabilityGroups.find((group) => group.id === activeId) || capabilityGroups[0];
  const ActiveIcon = active.icon;

  return (
    <div className="capability-system reveal">
      <div className="capability-nav" role="tablist" aria-label="WFast özellik alanları">
        <div className="capability-nav-head">
          <span><i /> KAYNAK KODU DENETLENDİ</span>
          <b>7 uzmanlık alanı</b>
          <small>Bir alan seçerek tüm yeteneklerini inceleyin.</small>
        </div>
        {capabilityGroups.map((group) => {
          const Icon = group.icon;
          const selected = group.id === active.id;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls="capability-detail"
              className={selected ? 'active' : ''}
              key={group.id}
              onClick={() => onSelect(group.id)}
            >
              <span><Icon size={17} /></span>
              <b>{group.short}</b>
              <small>{group.number}</small>
              <ChevronRight size={15} />
            </button>
          );
        })}
      </div>

      <article className="capability-detail" id="capability-detail" role="tabpanel" key={active.id}>
        <header>
          <div className="capability-title-icon"><ActiveIcon size={24} /></div>
          <div>
            <span>WFAST MODÜLÜ · {active.number}</span>
            <h3>{active.title}</h3>
            <p>{active.description}</p>
          </div>
        </header>

        <div className="capability-checks">
          {active.features.map(([title, text]) => (
            <div className="capability-check" key={title}>
              <span><Check size={13} /></span>
              <div><b>{title}</b><p>{text}</p></div>
            </div>
          ))}
        </div>

        <div className="capability-metrics">
          {active.metrics.map(([value, label]) => (
            <div key={label}><strong>{value}</strong><span>{label}</span></div>
          ))}
        </div>
      </article>
    </div>
  );
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeCapability, setActiveCapability] = useState('system');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => entry.isIntersecting && entry.target.classList.add('is-visible')),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [menuOpen]);

  useEffect(() => {
    const targetId = window.location.hash.slice(1);
    if (!targetId) return;
    const timer = window.setTimeout(() => document.getElementById(targetId)?.scrollIntoView(), 120);
    return () => window.clearTimeout(timer);
  }, []);

  const copyHash = async () => {
    await navigator.clipboard.writeText(sha256);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };

  return (
    <>
      <a className="skip-link" href="#main">İçeriğe geç</a>

      <div className="announcement">
        <span className="announcement-dot" />
        <b>WFast 1.0.0 hazır</b>
        <span>Sistem tepsisi, hızlı kısayollar ve arka planda oyun takibi</span>
        <a href="#tum-ozellikler">Tüm özellikleri keşfet <ArrowRight size={13} /></a>
      </div>

      <header className={`site-header${scrolled ? ' is-scrolled' : ''}`}>
        <div className="container header-inner">
          <Logo />
          <nav className={`main-nav${menuOpen ? ' is-open' : ''}`} aria-label="Ana menü">
            {navigation.map(([label, href]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}</a>
            ))}
            <DownloadButton className="nav-download" label="Ücretsiz indir" />
          </nav>
          <button className="menu-toggle" type="button" aria-label={menuOpen ? 'Menüyü kapat' : 'Menüyü aç'} aria-expanded={menuOpen} onClick={() => setMenuOpen(value => !value)}>
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero-grid-backdrop" />
          <div className="hero-orb hero-orb-one" />
          <div className="hero-orb hero-orb-two" />
          <div className="container hero-layout">
            <div className="hero-copy">
              <div className="hero-kicker"><BadgeCheck size={15} /> Windows 10 &amp; 11 için geliştirildi</div>
              <h1>Temizle.<br /><span>Odaklan.</span> Oyna.</h1>
              <p>WFast, gereksiz geçici verileri güvenle temizler; açtığınız oyunu otomatik algılar ve Windows kaynaklarını ölçülebilir, geri alınabilir bir oyun odağına taşır.</p>
              <div className="hero-actions">
                <DownloadButton label="Windows için indir" />
                <a className="button button-secondary" href="#oyun-modu">Nasıl çalışır? <ArrowRight size={17} /></a>
              </div>
              <div className="hero-meta">
                <span><Check size={14} /> Ücretsiz</span>
                <span><Check size={14} /> 64-bit</span>
                <span><Check size={14} /> 99.3 MB</span>
                <span><Check size={14} /> Geri alınabilir</span>
              </div>
            </div>
            <div className="hero-visual reveal is-visible">
              <ProductPreview />
            </div>
          </div>
          <div className="container trust-row">
            <span>OTOMATİK ALGILAMA</span>
            <div className="platform-list" aria-label="Desteklenen oyun platformları">
              <b>STEAM</b><i />
              <b>RIOT GAMES</b><i />
              <b>EPIC GAMES</b><i />
              <b>XBOX</b><i />
              <b>EA APP</b><i />
              <b>BATTLE.NET</b><i />
              <b>UBISOFT</b>
            </div>
          </div>
        </section>

        <section className="proof-strip" aria-label="WFast özeti">
          <div className="container proof-grid">
            <div><strong>20</strong><span>Arayüz ve kurulum dili</span></div>
            <div><strong>8</strong><span>Güvenli temizlik kategorisi</span></div>
            <div><strong>0</strong><span>DLL enjeksiyonu</span></div>
            <div><strong>%100</strong><span>Geri alınabilir oyun odağı</span></div>
          </div>
        </section>

        <section className="section features" id="ozellikler">
          <div className="container">
            <SectionHeading
              eyebrow="TEK PANEL, TAM KONTROL"
              title={<>Windows’un yükünü azaltın.<br />Oyuna daha fazlası kalsın.</>}
              text="Temizlikten kare süresi ölçümüne kadar her özellik, aynı sade kontrol merkezinde birlikte çalışır."
              align="center"
            />

            <div className="bento-grid">
              <article className="bento-card bento-wide reveal">
                <div className="bento-copy">
                  <span className="card-icon purple"><Radar size={19} /></span>
                  <small>AKILLI OYUN ALGILAMA</small>
                  <h3>EXE seçmeden. Oyunu aramadan.</h3>
                  <p>WFast, çalışan süreçleri puanlayarak launcher ve anti-cheat süreçlerini gerçek oyundan ayırır. Hangi oyunu açarsanız açın, oyun odağı doğru anda başlar.</p>
                  <ul><li><Check /> Popüler platform farkındalığı</li><li><Check /> Yanlış algılama koruması</li></ul>
                </div>
                <DetectionVisual />
              </article>

              <article className="bento-card reveal">
                <div className="bento-copy compact-copy">
                  <span className="card-icon green"><Gauge size={19} /></span>
                  <small>CANLI FPS KATMANI</small>
                  <h3>Gerçek performans, gözünüzün önünde.</h3>
                  <p>FPS ve kare süresini oyunun sağ üst köşesinde, tıklamaları engellemeden izleyin.</p>
                </div>
                <FpsVisual />
              </article>

              <article className="bento-card reveal">
                <div className="bento-copy compact-copy">
                  <span className="card-icon cyan"><Sparkles size={19} /></span>
                  <small>AKILLI TEMİZLİK</small>
                  <h3>Neyin silineceğini önce görün.</h3>
                  <p>Sekiz güvenli kategoriyi tarayın; dosya sayısını ve kazanılacak alanı temizlemeden önce inceleyin.</p>
                </div>
                <CleanerVisual />
              </article>

              <article className="bento-card bento-wide bento-reverse reveal">
                <RestoreVisual />
                <div className="bento-copy">
                  <span className="card-icon amber"><RefreshCw size={19} /></span>
                  <small>OTOMATİK GERİ YÜKLEME</small>
                  <h3>Oyun biter. Windows eski düzenine döner.</h3>
                  <p>Güç planınız ve süreç öncelikleriniz oyun başlamadan önce kaydedilir. Oyun kapandığında WFast değişiklikleri eksiksiz geri alır ve sıradaki oyunu bekler.</p>
                  <ul><li><Check /> Kalıcı sistem tweak’i yok</li><li><Check /> Tek tıkla manuel durdurma</li></ul>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="section game-mode" id="oyun-modu">
          <div className="container">
            <div className="game-mode-shell">
              <div className="game-mode-top">
                <div className="section-heading section-heading-light reveal">
                  <span className="eyebrow"><i />GAME FOCUS</span>
                  <h2>Bir oyun açın.<br /><em>Gerisini WFast’e bırakın.</em></h2>
                  <p>Arka plandaki sistemi körlemesine kapatmak yerine, güvenli süreçleri seçici biçimde yönetir ve aktif oyuna öncelik verir.</p>
                </div>
                <div className="focus-core-wrap reveal" aria-hidden="true">
                  <div className="focus-orbit orbit-one"><i /><i /><i /></div>
                  <div className="focus-orbit orbit-two"><i /><i /></div>
                  <div className="focus-core">
                    <Gamepad2 size={34} />
                    <span>GAME</span>
                    <b>FOCUS</b>
                    <small>AKTİF</small>
                  </div>
                  <span className="focus-badge badge-cpu"><Cpu size={14} /> CPU önceliği</span>
                  <span className="focus-badge badge-qos"><Zap size={14} /> High QoS</span>
                  <span className="focus-badge badge-fps"><Gauge size={14} /> 144 FPS</span>
                </div>
              </div>

              <div className="focus-steps">
                {focusSteps.map(({ icon: Icon, number, title, text }) => (
                  <div className="focus-step reveal" key={number}>
                    <div className="focus-step-head"><span><Icon size={17} /></span><small>{number}</small></div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                ))}
              </div>

              <div className="game-mode-note">
                <ShieldCheck size={18} />
                <span><b>Güvenli sınırlar içinde:</b> Windows’un kritik servisleri, Defender, ses, giriş aygıtları ve anti-cheat süreçleri korunur.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section intelligence">
          <div className="container intelligence-grid">
            <div>
              <SectionHeading
                eyebrow="DAHA AKILLI, DAHA ŞEFFAF"
                title="Sihirli düğmeler değil. Görülebilen sonuçlar."
                text="WFast, sisteminizi ölçer ve yaptığı her işi anlaşılır verilere dönüştürür. Ne değiştiğini, ne kazanıldığını ve ne zaman geri alındığını bilirsiniz."
              />
              <div className="intelligence-list">
                <div className="reveal"><span><CircleGauge /></span><b>Canlı sistem telemetrisi</b><p>CPU, bellek ve disk kullanımını anlık grafiklerle takip edin.</p></div>
                <div className="reveal"><span><HardDrive /></span><b>Temizlik önizlemesi</b><p>İşlemden önce kategori, dosya adedi ve toplam boyutu görün.</p></div>
                <div className="reveal"><span><ClipboardCheck /></span><b>Oturum özeti</b><p>Oyun odağının durumunu ve geri yükleme adımlarını izleyin.</p></div>
              </div>
            </div>
            <div className="telemetry-panel reveal" aria-label="CSS ile tasarlanmış sistem telemetrisi örneği">
              <div className="telemetry-header">
                <span><i /> CANLI TELEMETRİ</span>
                <small>SON 60 SN</small>
              </div>
              <div className="telemetry-score">
                <div className="score-ring"><strong>94</strong><span>SİSTEM<br />SKORU</span></div>
                <div><small>DURUM</small><b>Oyuna hazır</b><p>Tüm temel kontroller normal aralıkta.</p></div>
              </div>
              <div className="telemetry-chart">
                <div className="chart-labels"><span>100%</span><span>50%</span><span>0%</span></div>
                <div className="telemetry-grid"><i /><i /><i /><i /><i /><i /></div>
                <svg viewBox="0 0 520 150" preserveAspectRatio="none" aria-hidden="true">
                  <defs><linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7c6df2" stopOpacity=".34" /><stop offset="100%" stopColor="#7c6df2" stopOpacity="0" /></linearGradient></defs>
                  <path className="area" d="M0,112 C38,105 44,86 80,92 S134,118 166,87 S215,60 247,72 S291,101 328,70 S380,41 414,61 S468,79 520,35 L520,150 L0,150 Z" />
                  <path className="line" d="M0,112 C38,105 44,86 80,92 S134,118 166,87 S215,60 247,72 S291,101 328,70 S380,41 414,61 S468,79 520,35" />
                </svg>
              </div>
              <div className="telemetry-metrics">
                <span><i className="violet" /><b>CPU</b><strong>18%</strong></span>
                <span><i className="cyan" /><b>Bellek</b><strong>42%</strong></span>
                <span><i className="green" /><b>Disk</b><strong>7%</strong></span>
              </div>
            </div>
          </div>
        </section>

        <section className="section safety" id="guvenlik">
          <div className="container">
            <div className="safety-layout">
              <div className="safety-copy">
                <SectionHeading
                  eyebrow="GÜVENLİK TASARIMIN PARÇASI"
                  title="Performans için sistem bütünlüğünden vazgeçmeyin."
                  text="WFast’ın sınırları nettir. Kritik Windows bileşenlerine ve kişisel dosyalara dokunmadan, yalnızca geri alınabilir ve ölçülebilir adımlar uygular."
                />
                <SafetyVisual />
              </div>
              <div className="comparison reveal">
                <div className="comparison-head">
                  <span>YAKLAŞIM</span><b className="wfast-column"><span className="mini-mark"><Zap /></span> WFast</b><b>Kalıcı tweak araçları</b>
                </div>
                {[
                  ['Değişiklikleri otomatik geri alma', true, false],
                  ['Defender ve kritik servisleri koruma', true, false],
                  ['DLL enjeksiyonu olmadan FPS ölçümü', true, false],
                  ['Temizlik öncesi ayrıntılı önizleme', true, false],
                  ['Kişisel dosyaları kapsam dışında tutma', true, true]
                ].map(([label, safe, other]) => (
                  <div className="comparison-row" key={label}>
                    <span>{label}</span>
                    <b className="yes">{safe && <Check />}</b>
                    <b className={other ? 'partial' : 'no'}>{other ? 'Değişebilir' : <X />}</b>
                  </div>
                ))}
                <div className="comparison-foot"><LockKeyhole size={16} /> Oyun dosyalarınız değiştirilmez.</div>
              </div>
            </div>
          </div>
        </section>

        <section className="section languages">
          <div className="container language-shell">
            <div className="language-copy reveal">
              <span className="eyebrow"><i />DÜNYA ÇAPINDA HAZIR</span>
              <h2>20 dil.<br />Tek performans deneyimi.</h2>
              <p>Uygulama, kurulum ve sistem tepsisi; Türkçe, İngilizce, Almanca, Fransızca, İspanyolca, Basitleştirilmiş Çince, Japonca, Korece, İtalyanca, Felemenkçe, Rusça, Brezilya Portekizcesi, Lehçe, Ukraynaca, Arapça, Tayca, Vietnamca, Çekçe, İsveççe ve Macarca kullanılabilir.</p>
              <span className="language-status"><Globe2 size={16} /> Sistem dili kurulumda otomatik algılanır</span>
            </div>
            <LanguageVisual />
          </div>
        </section>

        <section className="section capabilities" id="tum-ozellikler">
          <div className="container">
            <SectionHeading
              eyebrow="WFAST YETKİNLİK MERKEZİ"
              title="Tek program. Yedi uzmanlık alanı."
              text="Aşağıdaki her yetenek WFast’ın çalışan kaynak kodu üzerinden denetlendi. Programın yaptığı işleri, uyguladığı sınırları ve sunduğu kontrolleri eksiksiz inceleyin."
              align="center"
            />
            <div className="capability-overview reveal">
              <div><strong>7</strong><span>Denetlenmiş modül</span></div>
              <div><strong>56</strong><span>Açıklanan temel yetenek</span></div>
              <div><strong>8</strong><span>Temizlik kategorisi</span></div>
              <div><strong>20</strong><span>Arayüz ve tepsi dili</span></div>
            </div>
            <CapabilityCenter activeId={activeCapability} onSelect={setActiveCapability} />
            <div className="capability-audit-note reveal">
              <span><ShieldCheck size={20} /></span>
              <div><b>Pazarlama iddiası değil, ürün denetimi.</b><p>Bu listede yalnızca WFast 1.0.0 içinde çalışan ve doğrulanabilen özellikler yer alır. Donanıma bağlı FPS artışı gibi garanti edilemeyen sonuçlar özellik olarak sunulmaz.</p></div>
              <a href="#guvenlik">Güvenlik yaklaşımı <ArrowRight size={15} /></a>
            </div>
          </div>
        </section>

        <section className="section download-section" id="indir">
          <div className="container download-shell">
            <div className="download-copy reveal">
              <span className="eyebrow eyebrow-light"><i />OYUNA HAZIR MISINIZ?</span>
              <h2>Windows’unuz.<br />Daha temiz, daha odaklı.</h2>
              <p>WFast’i indirin, güvenli taramayı başlatın ve oyunlarınızın otomatik algılanmasını tek tıkla etkinleştirin.</p>
              <div className="download-actions">
                <DownloadButton light label="WFast 1.0.0’ı indir" />
                <span><MonitorCheck size={17} /> Windows 10 / 11 · 64-bit</span>
              </div>
              <small className="download-disclaimer">Oyun modu ve sistem temizliği için yönetici izni gerekir.</small>
            </div>
            <div className="download-verification reveal">
              <div className="verification-head"><span><FileCheck2 size={22} /></span><div><small>DOSYA DOĞRULAMA</small><b>Orijinalliği kontrol edin</b></div></div>
              <div className="file-info"><span><Download size={15} /> WFast-Setup-1.0.0.exe</span><b>99.3 MB</b></div>
              <label>SHA-256</label>
              <div className="hash-box"><code>{sha256}</code><button type="button" onClick={copyHash} aria-label="SHA-256 kodunu kopyala">{copied ? <ClipboardCheck /> : <Copy />}</button></div>
              <p className={copied ? 'copy-status is-active' : 'copy-status'}>{copied ? 'SHA-256 panoya kopyalandı.' : 'İndirme sonrası dosya bütünlüğünü bu kodla doğrulayabilirsiniz.'}</p>
              <a href="/downloads/WFast-Setup-1.0.0.exe.sha256.txt" download>Doğrulama dosyasını indir <ArrowRight size={14} /></a>
            </div>
          </div>
        </section>

        <section className="section faq" id="sss">
          <div className="container faq-layout">
            <div className="faq-heading reveal">
              <span className="eyebrow"><i />MERAK EDİLENLER</span>
              <h2>Net sorular.<br />Net cevaplar.</h2>
              <p>Aradığınız yanıt burada değilse geliştiriciyle doğrudan iletişime geçebilirsiniz.</p>
              <a href="https://berkaykara.com" target="_blank" rel="noreferrer">berkaykara.com <ExternalLink size={15} /></a>
            </div>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <details className="reveal" key={faq.question} open={index === 0}>
                  <summary><span>{faq.question}</span><i><ChevronRight /></i></summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-main">
          <div className="footer-brand">
            <Logo compact />
            <p>Windows’u oyuna hazırlayan güvenli performans ve temizlik merkezi.</p>
            <span><ShieldCheck size={14} /> Windows 10 &amp; 11</span>
          </div>
          <div className="footer-links">
            <div><b>Ürün</b><a href="#ozellikler">Özellikler</a><a href="#oyun-modu">Oyun modu</a><a href="#tum-ozellikler">Tüm özellikler</a></div>
            <div><b>Kaynaklar</b><a href="#guvenlik">Güvenlik</a><a href="#sss">SSS</a><a href={downloadPath} download>Kurulumu indir</a></div>
            <div><b>Geliştirici</b><a href="https://berkaykara.com" target="_blank" rel="noreferrer">berkaykara.com <ExternalLink size={12} /></a></div>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© 2026 WFast. Tüm hakları saklıdır.</span>
          <a className="signature" href="https://berkaykara.com" target="_blank" rel="noreferrer" aria-label="bK. geliştirici web sitesi"><b>bK.</b> ile geliştirildi</a>
        </div>
      </footer>

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'WFast',
        applicationCategory: 'UtilitiesApplication',
        operatingSystem: 'Windows 10, Windows 11',
        softwareVersion: '1.0.0',
        downloadUrl: downloadPath,
        author: { '@type': 'Person', name: 'bK.', url: 'https://berkaykara.com' },
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'TRY' }
      })}</script>
    </>
  );
}
